import type { AudioEngine, MusicState, PlayOptions } from './engine';

export type { MusicState, PlayOptions };

let engine: AudioEngine | null = null;
/** Asked for before the engine existed; applied when it arrives. */
let music: MusicState = 'calm';
const readyListeners = new Set<(engine: AudioEngine) => void>();

/**
 * The game's one entry point to sound. Everything is a no-op until the first user gesture loads
 * the engine (see `unlock`), so callers never check whether audio exists, and a browser without
 * WebAudio simply stays silent. This module and `unlock` are all the home screen bundles.
 */
export const audio = {
  /** The live engine, or null before the first gesture. */
  get engine(): AudioEngine | null {
    return engine;
  },
  /** Play a cue; returns a voice handle for `stop`/`move`, or null (not loaded, merged, dropped). */
  play(id: string, opts?: PlayOptions): number | null {
    return engine?.play(id, opts) ?? null;
  },
  stop(handle: number, fadeMs?: number): void {
    engine?.stop(handle, fadeMs);
  },
  move(handle: number, x: number, y: number): void {
    engine?.move(handle, x, y);
  },
  /** Listener at the camera focus (sim units), facing the camera's yaw on the ground. */
  setListener(x: number, y: number, forwardX: number, forwardY: number): void {
    engine?.setListener(x, y, forwardX, forwardY);
  },
  setDead(dead: boolean): void {
    engine?.setDead(dead);
  },
  stopAll(): void {
    engine?.stopAll();
  },
  /** Music state (spec §7): calm on menus and in the shop, combat in a fight, etc. */
  setMusic(state: MusicState): void {
    music = state;
    engine?.setMusic(state);
  },
  /** Duck the music (dB, negative) for `holdMs`, releasing over `releaseMs`. */
  duck(db: number, holdMs: number, releaseMs: number): void {
    engine?.ducker.duck(db, holdMs, releaseMs);
  },
  /** Runs `fn` once the engine exists (at once if it already does). Returns an unsubscribe. */
  onReady(fn: (engine: AudioEngine) => void): () => void {
    if (engine) {
      fn(engine);
      return () => {};
    }
    readyListeners.add(fn);
    return () => readyListeners.delete(fn);
  },
};

/** Set by `unlock` once the lazy engine chunk has loaded; tests reset it with null. */
export function setAudioEngine(next: AudioEngine | null): void {
  engine = next;
  if (!next) return;
  next.setMusic(music);
  for (const fn of [...readyListeners]) {
    readyListeners.delete(fn);
    fn(next);
  }
}
